import {
  ButtonGroup,
  HorizontalBarChartV2,
  MappedDominoV2 as MappedDomino,
} from '@toggle/design-system';
import { Condition, Domino, Period, periods } from '@toggle/toggle';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ConditionSection } from '~/components/condition-section/ConditionSection';
import { TextBlock } from '~/components/text-block/TextBlock';
import { useChat } from '~/stores/use-chat/useChat';
import { createNewMessage } from '~/stores/use-chat/utils/updateMessage';
import { generateConditionString } from '~/utils/condition-utils/condition-utils';
import {
  getLargestEpisodeByPeriod,
  getPastEpisodesCount,
  getTradingDaysCount,
  MappedDominosByPeriod,
} from '~/utils/domino-utils/domino-utils';

import * as S from './DominoWidget.styles';

export interface DominoWidgetProps {
  conditions: Condition[];
  dominos: Domino[];
  mappedDominosByPeriod: MappedDominosByPeriod;
}

export const DominoWidget = ({
  conditions,
  mappedDominosByPeriod,
  dominos,
}: DominoWidgetProps) => {
  const { t } = useTranslation(['widget', 'scenarioCommander', 'analyze']);
  const [activePeriod, setActivePeriod] = useState<Period>(() => {
    const keys = Object.keys(mappedDominosByPeriod) as Period[];
    return keys.includes('3m') ? '3m' : keys[keys.length - 1];
  });
  const largestEpisode = getLargestEpisodeByPeriod(dominos, activePeriod);

  const append = useChat(state => state.append);

  const handleHorizonSelection = (i: number) => {
    setActivePeriod(periods[i]);
  };

  const horizonLabels = periods
    .filter(p => mappedDominosByPeriod[p])
    .map(s => s.toUpperCase());
  const activePeriodIndex = periods.indexOf(activePeriod);

  const onChartItemClick = (mappedDomino: MappedDomino) => {
    const [selectedDomino] = dominos.filter(
      domino => domino.entity === mappedDomino.tag
    );

    if (!selectedDomino) {
      return;
    }

    const entityName = mappedDomino.label;

    const newMessage = createNewMessage({
      status: 'complete',
      hasSeen: true,
      question: t('widget:scenario.scenarioWidgetQuestion', {
        entityName,
      }),
      reply: [
        {
          source: 'scenario-widget',
          data: {
            entityTag: selectedDomino.entity,
            conditions,
            episodes: selectedDomino.episodes,
            prediction: selectedDomino.prediction,
            confidence: 1,
          },
        },
      ],
    });
    append(newMessage);
  };

  const condition = generateConditionString({
    t,
    conditions: conditions as Condition[],
  });

  return (
    <S.DominoWidgetRoot data-testid="domino-widget">
      {largestEpisode && (
        <ConditionSection
          condition={condition}
          episodesCount={getPastEpisodesCount(largestEpisode)}
          totalTradingDays={getTradingDaysCount(largestEpisode)}
        />
      )}

      <S.ReactionSection>
        <TextBlock title={t('scenarioCommander:domino.reaction')}>
          <S.HorizonButtons>
            <ButtonGroup
              labels={horizonLabels}
              onClick={handleHorizonSelection}
              activeIndex={activePeriodIndex}
            />
          </S.HorizonButtons>
        </TextBlock>

        {mappedDominosByPeriod && (
          <HorizontalBarChartV2
            data={mappedDominosByPeriod[activePeriod]}
            onChartItemClick={onChartItemClick}
            medianLabel={t('widget:domino.median')}
            viewAllAssetsText={t('widget:domino.viewAllAssetsText')}
            viewLessAssetsText={t('widget:domino.viewLessAssetsText')}
          />
        )}
      </S.ReactionSection>
    </S.DominoWidgetRoot>
  );
};
